import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Search, Trash2, Edit2, RotateCcw, Archive as ArchiveIcon, CalendarRange } from 'lucide-react';
import {
  API, PLATFORMS, STATUSES, STATUS_COLORS, PLATFORM_SHORT, PLATFORM_COLORS, PAGE_SIZE, fmt,
} from './constants';
import { ConfirmDialog } from './ConfirmDialog';

export const ArchivePanel = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');
  const [platform, setPlatform] = useState('all');
  const [status, setStatus] = useState('all');
  const [range, setRange] = useState('all'); // all | this_month | last_month | last_3_months | custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ theme: '', post_idea: '', platform: '', notes: '', status: 'Posted' });
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [pendingRestore, setPendingRestore] = useState(null);

  const before = fmt(new Date()); // client's today; archive is strictly before this

  // Resolve the active date-range preset into {start, end} (YYYY-MM-DD).
  const resolveRange = useCallback(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const first = (yr, mo) => fmt(new Date(yr, mo, 1));
    const last = (yr, mo) => fmt(new Date(yr, mo + 1, 0));
    switch (range) {
      case 'this_month': return { start: first(y, m), end: last(y, m) };
      case 'last_month': return { start: first(y, m - 1), end: last(y, m - 1) };
      case 'last_3_months': return { start: first(y, m - 2), end: last(y, m) };
      case 'custom': return { start: customStart || undefined, end: customEnd || undefined };
      default: return { start: undefined, end: undefined };
    }
  }, [range, customStart, customEnd]);

  const fetchPage = useCallback(async (skip) => {
    const params = new URLSearchParams();
    params.set('before', before);
    if (platform !== 'all') params.set('platform', platform);
    if (status !== 'all') params.set('status', status);
    if (query) params.set('q', query);
    const { start, end } = resolveRange();
    if (start) params.set('start_date', start);
    if (end) params.set('end_date', end);
    params.set('skip', String(skip));
    params.set('limit', String(PAGE_SIZE));
    const res = await axios.get(`${API}/content/archive?${params.toString()}`, getAuthHeaders());
    return res.data;
  }, [getAuthHeaders, before, platform, status, query, resolveRange]);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPage(0);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) { console.error(e); }
  }, [fetchPage]);

  useEffect(() => {
    const t = setTimeout(refresh, 200);
    return () => clearTimeout(t);
  }, [refresh]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(items.length);
      setItems(prev => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch (e) { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      theme: plan.theme || '', post_idea: plan.post_idea || '',
      platform: plan.platform || '', notes: plan.notes || '', status: plan.status || 'Posted',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      await axios.put(`${API}/content/planner/${editing.plan_id}`, form, getAuthHeaders());
      toast.success('Updated');
      setDialogOpen(false);
      refresh();
    } catch (e) { toast.error('Failed to save'); }
  };

  const remove = async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`${API}/content/planner/${pendingDeleteId}`, getAuthHeaders());
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error('Failed'); }
    finally { setPendingDeleteId(null); }
  };

  const restore = async () => {
    if (!pendingRestore) return;
    try {
      await axios.post(`${API}/content/planner`, {
        date: before, // bring it back onto today's planner
        theme: pendingRestore.theme || '',
        post_idea: pendingRestore.post_idea || '',
        platform: pendingRestore.platform || '',
        notes: pendingRestore.notes || '',
        status: 'Idea',
      }, getAuthHeaders());
      toast.success('Restored to today\u2019s planner');
      refresh();
    } catch (e) { toast.error('Failed to restore'); }
    finally { setPendingRestore(null); }
  };

  const fmtDate = (dateStr) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });

  const hasFilters = query || platform !== 'all' || status !== 'all' || range !== 'all';

  return (
    <div className="space-y-3" data-testid="archive-panel">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search past posts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="archive-search"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger data-testid="archive-platform-filter"><SelectValue placeholder="Platform" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="archive-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <CalendarRange className="w-4 h-4 text-sky-600 shrink-0" />
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="flex-1" data-testid="archive-range-filter"><SelectValue placeholder="Date range" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="this_month">This month</SelectItem>
            <SelectItem value="last_month">Last month</SelectItem>
            <SelectItem value="last_3_months">Last 3 months</SelectItem>
            <SelectItem value="custom">Custom range…</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {range === 'custom' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[11px] text-muted-foreground">From</Label>
            <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="archive-custom-start" />
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">To</Label>
            <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="archive-custom-end" />
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <ArchiveIcon className="w-8 h-8 mx-auto mb-2 text-sky-400" />
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? 'No past posts match your filters.'
                : 'Your past content will appear here once its date has passed. Look back anytime to see what you posted before.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground text-right" data-testid="archive-count">
            Showing {items.length} of {total}
          </p>
          <div className="space-y-2">
            {items.map(plan => (
              <Card key={plan.plan_id} className="border-sky-100 dark:border-sky-900" data-testid={`archive-item-${plan.plan_id}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        <span className="text-[11px] font-medium text-sky-700 dark:text-sky-300">{fmtDate(plan.date)}</span>
                        {plan.platform && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${PLATFORM_COLORS[plan.platform] || 'bg-sky-100 text-sky-700'}`}>
                            {PLATFORM_SHORT[plan.platform] || plan.platform}
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${STATUS_COLORS[plan.status] || STATUS_COLORS.Idea}`}>
                          {plan.status}
                        </span>
                        {plan.theme && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                            {plan.theme}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium break-words whitespace-pre-wrap">
                        {plan.post_idea || <span className="italic text-muted-foreground">Untitled idea</span>}
                      </p>
                      {plan.notes && <p className="text-xs text-muted-foreground mt-1 break-words whitespace-pre-wrap">{plan.notes}</p>}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(plan)} data-testid={`archive-edit-${plan.plan_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-sky-600" onClick={() => setPendingRestore(plan)} data-testid={`archive-restore-${plan.plan_id}`} title="Restore to today">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setPendingDeleteId(plan.plan_id)} data-testid={`archive-delete-${plan.plan_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {hasMore && (
            <Button
              variant="outline"
              className="w-full border-sky-300 text-sky-600"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="archive-load-more"
            >
              {loadingMore ? 'Loading...' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
            </Button>
          )}
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Archived Post</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Theme</Label>
              <Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} data-testid="archive-theme-input" />
            </div>
            <div>
              <Label>Post Idea</Label>
              <Textarea value={form.post_idea} rows={2} onChange={(e) => setForm({ ...form, post_idea: e.target.value })} data-testid="archive-idea-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Platform</Label>
                <Select value={form.platform || undefined} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger data-testid="archive-platform-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="archive-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} rows={3} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="archive-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="archive-save-btn">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
        onConfirm={remove}
        title="Delete this post from archive?"
        description="This will permanently remove the post from your history."
        testId="archive-confirm-delete"
      />

      <ConfirmDialog
        open={!!pendingRestore}
        onOpenChange={(o) => !o && setPendingRestore(null)}
        onConfirm={restore}
        title="Restore to today's planner?"
        description="A copy of this post will be added to today's planner as an Idea so you can reuse or reschedule it. The original stays in your archive."
        confirmText="Restore"
        testId="archive-confirm-restore"
      />
    </div>
  );
};

export default ArchivePanel;
